package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea

private val field = Bem("admin-field")
private val input = Bem("admin-input")
private val textarea = Bem("admin-textarea")

/**
 * A labelled single-line input with an optional hint, or an error in its place.
 * An error sets `aria-invalid` and is linked with `aria-describedby`, which is also what the styles read.
 */
@Composable
fun AdminTextField(
    id: String,
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    type: InputType<String> = InputType.Text,
    error: String? = null,
    hint: String? = null,
    optional: Boolean = false,
    autocomplete: String? = null,
    placeholder: String? = null,
    inputMode: String? = null,
    onBlur: (() -> Unit)? = null,
    /** Whether it carries `required`; a field may be wanted without being required or marked optional (#105). */
    required: Boolean = !optional,
    /** Whether only the new website shows it, which a [BetaBadge] after the label says (#144). */
    beta: Boolean = false,
) {
    val messageId = "$id-message"

    Div({ classes(field.block) }) {
        Label(forId = id, attrs = { classes(field.element("label").className) }) {
            Text(label)
            if (beta) {
                BetaBadge()
            }
            if (optional) {
                Text(" ")
                Span({ classes(field.element("optional").className) }) { Text("(optional)") }
            }
        }

        Input(type) {
            classes(input.block)
            id(id)
            name(id)
            value(value)
            onInput { onValueChange(it.value) }
            if (required) {
                required()
            }
            autocomplete?.let { attr("autocomplete", it) }
            placeholder?.let { placeholder(it) }
            inputMode?.let { attr("inputmode", it) }
            onBlur?.let { handler -> onBlur { handler() } }
            if (error != null) {
                attr("aria-invalid", "true")
            }
            if (error != null || hint != null) {
                attr("aria-describedby", messageId)
            }
        }

        when {
            error != null -> Span({
                classes(field.element("error").className)
                id(messageId)
            }) { Text(error) }

            hint != null -> Span({
                classes(field.element("hint").className)
                id(messageId)
            }) { Text(hint) }
        }
    }
}

/** [AdminTextField] for plain text of several lines, such as an address or a short text. */
@Composable
fun AdminTextAreaField(
    id: String,
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    rows: Int = 3,
    error: String? = null,
    hint: String? = null,
    optional: Boolean = false,
    /** Whether only the new website shows it, which a [BetaBadge] after the label says (#144). */
    beta: Boolean = false,
) {
    val messageId = "$id-message"

    Div({ classes(field.block) }) {
        Label(forId = id, attrs = { classes(field.element("label").className) }) {
            Text(label)
            if (beta) {
                BetaBadge()
            }
            if (optional) {
                Text(" ")
                Span({ classes(field.element("optional").className) }) { Text("(optional)") }
            }
        }

        TextArea(value) {
            classes(textarea.block)
            id(id)
            name(id)
            attr("rows", rows.toString())
            onInput { onValueChange(it.value) }
            if (!optional) {
                required()
            }
            if (error != null) {
                attr("aria-invalid", "true")
            }
            if (error != null || hint != null) {
                attr("aria-describedby", messageId)
            }
        }

        when {
            error != null -> Span({
                classes(field.element("error").className)
                id(messageId)
            }) { Text(error) }

            hint != null -> Span({
                classes(field.element("hint").className)
                id(messageId)
            }) { Text(hint) }
        }
    }
}

/**
 * Plain text for a person who may not change it, like [AdminMarkdownView] (design system v43, "Read-only value"): the
 * label and the value, line breaks kept, or [empty] in the hint's colour when there is none.
 */
@Composable
fun AdminTextView(id: String, label: String, value: String, empty: String = "Nichts angegeben.") {
    val labelId = "$id-label"

    Div({ classes(field.block) }) {
        Span({
            classes(field.element("label").className)
            id(labelId)
        }) { Text(label) }
        P({
            classes(field.element("value").className)
            if (value.isBlank()) {
                classes(field.element("value").modifier("empty"))
            }
            id(id)
            attr("aria-labelledby", labelId)
        }) { Text(value.ifBlank { empty }) }
    }
}
